import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin, Statistic, Typography } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { parseJsonArray, recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;
const COMMON_ALLOWANCES = ['Housing', 'Transport', 'Food', 'Telephone', 'Other'];

const OfferFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [appSearch, setAppSearch] = useState('');
  const presetApp = sp.get('application_id') || undefined;
  const { data: departments = [] } = useDepartmentsForSelection();

  const { data: existing, isLoading } = useQuery({
    queryKey: hrmsKeys.offer(id || ''),
    queryFn: () => hrmsService.getOffer(id!),
    enabled: isEdit,
  });
  const { data: apps, isFetching } = useQuery({
    queryKey: hrmsKeys.applications({ picker: appSearch, offer: 1 }),
    queryFn: () => hrmsService.pageApplications({ page: 1, limit: 20, q: appSearch || undefined, statuses: 'Interview,Offer,Screening' }),
    enabled: !isEdit,
  });
  const applicationId = Form.useWatch('application_id', form);
  const { data: app } = useQuery({
    queryKey: hrmsKeys.application(applicationId || presetApp || ''),
    queryFn: () => hrmsService.getApplication((applicationId || presetApp)!),
    enabled: Boolean(applicationId || presetApp) && !isEdit,
  });
  const { data: positions = [] } = useQuery({ queryKey: hrmsKeys.positions(), queryFn: () => hrmsService.listPositions() });
  const { data: grades = [] } = useQuery({ queryKey: hrmsKeys.grades(), queryFn: () => hrmsService.listGrades() });

  useEffect(() => {
    if (existing) {
      form.setFieldsValue({
        ...existing,
        join_date: existing.join_date ? dayjs(existing.join_date) : undefined,
        basic_salary: existing.basic_salary != null ? Number(existing.basic_salary) : undefined,
        allowances: parseJsonArray(existing.allowances_json).map((a: any) => ({ name: a.name, amount: Number(a.amount) })),
      });
    } else if (presetApp) {
      form.setFieldValue('application_id', presetApp);
    }
  }, [existing, presetApp, form]);

  useEffect(() => {
    if (isEdit || !app?.job) return;
    const cur = form.getFieldsValue();
    form.setFieldsValue({
      job_title: cur.job_title || app.job.title,
      department_id: cur.department_id || app.job.department_id || undefined,
      position_id: cur.position_id || app.job.position_id || undefined,
      grade_id: cur.grade_id || app.job.grade_id || undefined,
      employment_type: cur.employment_type || app.job.employment_type || 'Full-time',
    });
  }, [app, isEdit, form]);

  const basic = Form.useWatch('basic_salary', form) || 0;
  const allowances = (Form.useWatch('allowances', form) || []) as Array<{ amount?: number }>;
  const currency = Form.useWatch('currency', form) || 'AED';
  const total = Number(basic) + allowances.reduce((s, a) => s + (Number(a?.amount) || 0), 0);

  const appOptions = isEdit
    ? existing
      ? [{ value: existing.application_id, label: `${existing.application_number} — ${existing.candidate_name}` }]
      : []
    : [
        ...(app ? [{ value: app.id, label: `${app.application_number} — ${app.candidate_name} (${app.job_title || ''})` }] : []),
        ...(apps?.rows || [])
          .filter((a: any) => a.id !== app?.id)
          .map((a: any) => ({ value: a.id, label: `${a.application_number} — ${a.candidate_name} (${a.job_title || ''})` })),
      ];

  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const body = {
        ...v,
        allowances: undefined,
        allowances_json: (v.allowances || []).filter((a: any) => a?.name && a.amount != null),
        join_date: v.join_date ? dayjs(v.join_date).format('YYYY-MM-DD') : null,
        contingency_medical: !!v.contingency_medical,
        contingency_visa: !!v.contingency_visa,
        contingency_background: !!v.contingency_background,
      };
      const saved = isEdit ? await hrmsService.updateOffer(id!, body) : await hrmsService.createOffer(body);
      message.success(isEdit ? 'Offer updated' : `Offer ${saved.offer_number} created`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.offers() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.offer(saved.id) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.application(saved.application_id) });
      navigate(`/hr/offers/${saved.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Offer" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  const locked = isEdit && existing && !['Draft', 'Rejected'].includes(existing.status);

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? `Edit ${existing?.offer_number || 'offer'}` : 'New offer'}
          subtitle="Compensation package — submit for approval, then it is emailed to the candidate"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={Boolean(locked)} onClick={save}>
                Save draft
              </Button>
            </Space>
          }
        />
      }
    >
      {locked && <Alert type="warning" showIcon style={{ marginBottom: 16, maxWidth: 960 }} message={`This offer is ${existing.status} and can no longer be edited. Withdraw it and create a new one to change terms.`} />}
      <Form
        form={form}
        layout="vertical"
        disabled={Boolean(locked)}
        style={{ maxWidth: 960 }}
        initialValues={{ currency: 'AED', probation_months: 6, employment_type: 'Full-time', contract_type: 'Limited', allowances: [] }}
      >
        <Card title="Candidate and role" style={{ marginBottom: 16 }}>
          <Form.Item name="application_id" label="Application" rules={[{ required: true, message: 'Select the application' }]}>
            <Select
              showSearch
              disabled={isEdit}
              filterOption={false}
              onSearch={setAppSearch}
              notFoundContent={isFetching ? <Spin size="small" /> : 'No applications at interview / offer stage'}
              placeholder="Search candidate or application number"
              options={appOptions}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="job_title" label="Job title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="department_id"
                label="Department"
                rules={[{ required: true, message: 'Department is required — it routes the approval and the onboarding checklist' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={departments.map((d: any) => ({ value: d.id, label: d.department_name || d.name || d.code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="position_id" label="Position">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={positions.map((p: any) => ({ value: p.id, label: p.position_name || p.name || p.position_code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="grade_id" label="Grade">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={grades.map((g: any) => ({ value: g.id, label: g.grade_name || g.name || g.grade_code }))}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Terms" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="join_date" label="Proposed join date" extra="Required before submitting">
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="employment_type" label="Employment type">
                <Select options={['Full-time', 'Part-time', 'Contract', 'Intern'].map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="contract_type" label="Contract type">
                <Select options={['Limited', 'Unlimited'].map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="probation_months" label="Probation (months)">
                <InputNumber min={0} max={12} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Text strong>Conditions of the offer</Text>
          <div style={{ marginTop: 8 }}>
            <Space wrap size="large">
              <Form.Item name="contingency_medical" valuePropName="checked" noStyle>
                <Checkbox>Medical fitness</Checkbox>
              </Form.Item>
              <Form.Item name="contingency_visa" valuePropName="checked" noStyle>
                <Checkbox>Visa / work permit</Checkbox>
              </Form.Item>
              <Form.Item name="contingency_background" valuePropName="checked" noStyle>
                <Checkbox>Background and reference checks</Checkbox>
              </Form.Item>
            </Space>
          </div>
        </Card>

        <Card title="Compensation (monthly)" style={{ marginBottom: 16 }} extra={<Statistic value={total} precision={2} prefix={`${currency} `} valueStyle={{ fontSize: 18 }} title="Gross monthly" />}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="basic_salary" label="Basic salary" rules={[{ required: true, message: 'Basic salary is required' }, { type: 'number', min: 1, message: 'Must be greater than zero' }]}>
                <InputNumber min={0} style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={4}>
              <Form.Item name="currency" label="Currency">
                <Input maxLength={3} style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.List name="allowances">
            {(fields, { add, remove }) => (
              <>
                {fields.map((f) => (
                  <Row gutter={8} key={f.key} align="middle">
                    <Col xs={12} sm={8}>
                      <Form.Item name={[f.name, 'name']} rules={[{ required: true, message: 'Name' }]}>
                        <Select
                          showSearch
                          placeholder="Allowance"
                          options={COMMON_ALLOWANCES.map((a) => ({ value: a, label: a }))}
                          mode={undefined}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={9} sm={6}>
                      <Form.Item name={[f.name, 'amount']} rules={[{ required: true, message: 'Amount' }]}>
                        <InputNumber min={0} style={{ width: '100%' }} placeholder="Amount" />
                      </Form.Item>
                    </Col>
                    <Col xs={3} sm={2}>
                      <Form.Item>
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(f.name)} />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ amount: 0 })}>
                  Add allowance
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card title="Notes">
          <Form.Item name="notes" style={{ marginBottom: 0 }}>
            <Input.TextArea rows={3} placeholder="Internal notes (not shown on the offer letter)" />
          </Form.Item>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default OfferFormPage;
